/**
 * Request Tester Component
 * Simple UI for testing API endpoints with auto-discovery from OpenAPI spec
 */

import { useState, useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  makeStyles,
  tokens,
  mergeClasses,
  Button,
  Dropdown,
  Option,
  Input,
  Textarea,
  Text,
  Spinner,
  Badge,
  Tooltip,
} from '@fluentui/react-components'
import { SendRegular, AddRegular, DismissRegular, CopyRegular } from '@fluentui/react-icons'
import { useOpenApiSpec, type ApiEndpoint } from '../hooks/useOpenApiSpec'
import { getAccessToken } from '../../../services/apiService'

type ParamType = 'Path' | 'Query' | 'Header'

interface ParamRow {
  id: string
  type: ParamType
  name: string
  value: string
  required: boolean
}

interface ApiResponse {
  status: number
  statusText: string
  body: string
  headers: Record<string, string>
}

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']

const useStyles = makeStyles({
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalXL,
    padding: tokens.spacingHorizontalXL,
    height: '100%',
    overflow: 'hidden',
  },
  requestPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    overflow: 'auto',
    paddingRight: tokens.spacingHorizontalM,
  },
  responsePanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    overflow: 'hidden',
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingLeft: tokens.spacingHorizontalXL,
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  panelTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
  },
  requestSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  requestRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'flex-start',
  },
  methodDropdown: {
    minWidth: '110px',
  },
  endpointDropdown: {
    flex: 1,
    minWidth: '200px',
  },
  sendButton: {
    minWidth: '90px',
  },
  paramsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  paramsTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  paramsHeader: {
    display: 'grid',
    gridTemplateColumns: '90px 150px 1fr 32px',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground3,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  paramsRow: {
    display: 'grid',
    gridTemplateColumns: '90px 150px 1fr 32px',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingHorizontalS,
    alignItems: 'center',
    '&:not(:last-child)': {
      borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
  },
  paramsRowRequired: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  removeButton: {
    minWidth: 'auto',
    padding: '0',
  },
  addButtonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: tokens.spacingVerticalXS,
  },
  bodySection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  sectionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  bodyTextarea: {
    width: '100%',
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  responseBody: {
    flex: 1,
    width: '100%',
    padding: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'auto',
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxSizing: 'border-box',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    flex: 1,
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    width: '100%',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxSizing: 'border-box',
  },
  emptyHint: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  urlPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  urlText: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: tokens.colorNeutralForeground2,
  },
  copyButton: {
    minWidth: 'auto',
    flexShrink: 0,
  },
  endpointOption: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  endpointOptionName: {
    fontWeight: tokens.fontWeightSemibold,
  },
  endpointOptionPath: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  methodBadge: {
    fontSize: tokens.fontSizeBase100,
    padding: '0 4px',
    borderRadius: tokens.borderRadiusSmall,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
  },
  methodGet: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
  },
  methodPost: {
    backgroundColor: tokens.colorPaletteBerryBackground2,
    color: tokens.colorPaletteBerryForeground2,
  },
  methodPut: {
    backgroundColor: tokens.colorPaletteMarigoldBackground2,
    color: tokens.colorPaletteMarigoldForeground2,
  },
  methodPatch: {
    backgroundColor: tokens.colorPalettePeachBackground2,
    color: tokens.colorPalettePeachForeground2,
  },
  methodDelete: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground2,
  },
  typeDropdown: {
    minWidth: '80px',
  },
  nameInput: {
    minWidth: '100px',
  },
  valueInput: {
    minWidth: '100px',
  },
  tooltipContent: {
    fontSize: tokens.fontSizeBase200,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  fullPageLoader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    height: '100%',
    width: '100%',
  },
})

function getStatusColor(status: number): 'success' | 'warning' | 'danger' | 'informative' {
  if (status >= 200 && status < 300) return 'success'
  if (status >= 300 && status < 400) return 'informative'
  if (status >= 400 && status < 500) return 'warning'
  return 'danger'
}

function extractPathParams(path: string): string[] {
  const matches = path.match(/\{([^}]+)\}/g)
  return matches ? matches.map((m) => m.slice(1, -1)) : []
}

function sortParams(params: ParamRow[]): ParamRow[] {
  const typeOrder: Record<ParamType, number> = { Path: 0, Query: 1, Header: 2 }
  return [...params].sort((a, b) => {
    if (typeOrder[a.type] !== typeOrder[b.type]) {
      return typeOrder[a.type] - typeOrder[b.type]
    }
    return a.name.localeCompare(b.name)
  })
}

/**
 * Builds request URL with path param substitution and query params
 * Uses URL API for safe path joining (prevents double slashes)
 */
function buildRequestUrl(
  pathTemplate: string,
  pathParams: ParamRow[],
  queryParams: ParamRow[]
): string {
  // Substitute path params
  let path = pathTemplate
  for (const param of pathParams) {
    path = path.replace(`{${param.name}}`, encodeURIComponent(param.value))
  }

  // Use URL API for safe construction (handles base URL joining properly)
  const url = new URL(path, window.location.origin)

  // Add query params
  for (const param of queryParams) {
    if (param.name.trim()) {
      url.searchParams.append(param.name.trim(), param.value)
    }
  }

  // Return pathname + search (relative URL for fetch)
  return url.pathname + url.search
}

function getEndpointDisplayName(endpoint: ApiEndpoint): string {
  if (endpoint.operationId) {
    return endpoint.operationId
  }
  // Derive from path: /api/v1/chat/conversations/{id}/messages → Chat.Conversations.Messages
  const segments = endpoint.path
    .replace(/^\/api\/v\d+\//, '')
    .split('/')
    .filter((s) => s && !s.startsWith('{'))
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
  return segments.join('.') || endpoint.path
}

function getMethodBadgeClass(
  method: string,
  styles: ReturnType<typeof useStyles>
): string {
  const methodMap: Record<string, string> = {
    GET: styles.methodGet,
    POST: styles.methodPost,
    PUT: styles.methodPut,
    PATCH: styles.methodPatch,
    DELETE: styles.methodDelete,
  }
  return `${styles.methodBadge} ${methodMap[method] || ''}`
}

function formatJson(text: string): string {
  try {
    const parsed = JSON.parse(text)
    return JSON.stringify(parsed, null, 2)
  } catch {
    return text
  }
}

export function RequestTester() {
  const styles = useStyles()
  const [searchParams, setSearchParams] = useSearchParams()
  const { endpoints, isLoading: isLoadingSpec, error: specError } = useOpenApiSpec()

  const [method, setMethod] = useState(() => searchParams.get('method') || 'GET')
  const [selectedEndpoint, setSelectedEndpoint] = useState<ApiEndpoint | null>(null)
  const [params, setParams] = useState<ParamRow[]>([])
  const [body, setBody] = useState('')
  const [response, setResponse] = useState<ApiResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [initialized, setInitialized] = useState(false)

  // Initialize selection from URL params or default to first endpoint
  useEffect(() => {
    if (endpoints.length > 0 && !initialized) {
      const pathParam = searchParams.get('path')
      const methodParam = searchParams.get('method')

      let endpoint: ApiEndpoint | undefined

      if (pathParam && methodParam) {
        endpoint = endpoints.find((e) => e.path === pathParam && e.method === methodParam)
      }

      if (!endpoint) {
        endpoint = endpoints[0]
      }

      if (endpoint) {
        setSelectedEndpoint(endpoint)
        setMethod(endpoint.method)
        setSearchParams({ method: endpoint.method, path: endpoint.path }, { replace: true })

        // Auto-populate body with example if available
        if (endpoint.requestBodyExample && ['POST', 'PUT', 'PATCH'].includes(endpoint.method)) {
          setBody(endpoint.requestBodyExample)
        }

        // Initialize path params for the endpoint
        const pathParamNames = extractPathParams(endpoint.path)
        const newParams: ParamRow[] = pathParamNames.map((name) => ({
          id: crypto.randomUUID(),
          type: 'Path' as ParamType,
          name,
          value: '',
          required: true,
        }))
        setParams(sortParams(newParams))
      }

      setInitialized(true)
    }
  }, [endpoints, initialized, searchParams, setSearchParams])

  const currentPath = selectedEndpoint?.path || ''

  const resolvedUrl = useMemo(() => {
    if (!currentPath) return ''

    // Build preview URL - keep placeholders for empty path params
    let path = currentPath
    for (const param of params.filter((p) => p.type === 'Path')) {
      if (param.value.trim()) {
        path = path.replace(`{${param.name}}`, encodeURIComponent(param.value))
      }
      // If empty, keep the {name} placeholder as-is
    }

    // Add query params
    const queryParams = params.filter((p) => p.type === 'Query' && p.name.trim())
    if (queryParams.length > 0) {
      const searchParams = new URLSearchParams()
      for (const param of queryParams) {
        searchParams.append(param.name.trim(), param.value)
      }
      path += '?' + searchParams.toString()
    }

    return path
  }, [currentPath, params])

  const copyUrl = useCallback(() => {
    navigator.clipboard.writeText(window.location.origin + resolvedUrl)
  }, [resolvedUrl])

  const updateSelection = useCallback(
    (endpoint: ApiEndpoint) => {
      setSelectedEndpoint(endpoint)
      setMethod(endpoint.method)
      setSearchParams({ method: endpoint.method, path: endpoint.path }, { replace: true })

      // Auto-populate body with example if available
      if (endpoint.requestBodyExample && ['POST', 'PUT', 'PATCH'].includes(endpoint.method)) {
        setBody(endpoint.requestBodyExample)
      } else {
        setBody('')
      }

      // Reset params - create required path params from template, clear query/header
      const pathParamNames = extractPathParams(endpoint.path)
      const newParams: ParamRow[] = pathParamNames.map((name) => ({
        id: crypto.randomUUID(),
        type: 'Path' as ParamType,
        name,
        value: '',
        required: true,
      }))
      setParams(sortParams(newParams))
    },
    [setSearchParams]
  )

  const addParam = useCallback(() => {
    setParams((prev) => [
      ...prev,
      { id: crypto.randomUUID(), type: 'Query', name: '', value: '', required: false },
    ])
  }, [])

  const removeParam = useCallback((id: string) => {
    setParams((prev) => prev.filter((p) => p.id !== id))
  }, [])

  const updateParam = useCallback(
    (id: string, field: 'type' | 'name' | 'value', value: string) => {
      setParams((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)))
    },
    []
  )

  const sendRequest = useCallback(async () => {
    if (!currentPath) {
      setError('Please select an endpoint')
      return
    }

    // Clear previous state FIRST (before validation)
    setError(null)
    setResponse(null)

    // Validate path params
    const pathParams = params.filter((p) => p.type === 'Path')
    const emptyPathParams = pathParams.filter((p) => !p.value.trim()).map((p) => p.name)

    if (emptyPathParams.length > 0) {
      setError(`Missing required path parameters: ${emptyPathParams.join(', ')}`)
      return
    }

    setIsLoading(true)

    try {
      const accessToken = await getAccessToken()
      const queryParams = params.filter((p) => p.type === 'Query')
      const headerParams = params.filter((p) => p.type === 'Header')

      // Build URL using helper (safe path construction)
      const url = buildRequestUrl(currentPath, pathParams, queryParams)

      const requestHeaders: Record<string, string> = {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      }

      // Add custom headers
      for (const header of headerParams) {
        if (header.name.trim()) {
          requestHeaders[header.name.trim()] = header.value
        }
      }

      const options: RequestInit = {
        method,
        headers: requestHeaders,
      }

      if (body.trim() && ['POST', 'PUT', 'PATCH'].includes(method)) {
        try {
          // Parse and normalize JSON (handles any remaining double-encoding)
          let parsed = JSON.parse(body)
          // If we got a string, it might be double-encoded JSON - try parsing again
          if (typeof parsed === 'string') {
            try {
              parsed = JSON.parse(parsed)
            } catch {
              // Not double-encoded, use the string value
            }
          }
          options.body = JSON.stringify(parsed)
        } catch {
          // If parsing fails entirely, send as-is (user sees server error)
          options.body = body
        }
      }

      const res = await fetch(url, options)
      const responseText = await res.text()

      // Extract response headers
      const responseHeaders: Record<string, string> = {}
      res.headers.forEach((value, key) => {
        responseHeaders[key] = value
      })

      setResponse({
        status: res.status,
        statusText: res.statusText,
        body: responseText,
        headers: responseHeaders,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed')
    } finally {
      setIsLoading(false)
    }
  }, [method, currentPath, params, body])

  if (isLoadingSpec) {
    return (
      <div className={styles.fullPageLoader}>
        <Spinner size="large" />
        <Text>Loading API endpoints...</Text>
      </div>
    )
  }

  if (specError) {
    return (
      <div className={styles.fullPageLoader}>
        <Text className={styles.errorText}>Failed to load OpenAPI spec</Text>
        <Text>{specError}</Text>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {/* Left Panel - Request Configuration */}
      <div className={styles.requestPanel}>
        <div className={styles.panelHeader}>
          <Text className={styles.panelTitle}>Request</Text>
        </div>

        {/* Endpoint Selection */}
        <div className={styles.requestSection}>
          <div className={styles.requestRow}>
            <Dropdown
              className={styles.methodDropdown}
              value={method}
              selectedOptions={[method]}
              onOptionSelect={(_, data) => {
                if (data.optionValue) {
                  const newMethod = data.optionValue
                  setMethod(newMethod)
                  // Find matching endpoint with new method if available
                  if (selectedEndpoint) {
                    const match = endpoints.find(
                      (e) => e.path === selectedEndpoint.path && e.method === newMethod
                    )
                    if (match) {
                      updateSelection(match)
                    } else {
                      setSearchParams({ method: newMethod, path: selectedEndpoint.path }, { replace: true })
                    }
                  }
                }
              }}
            >
              {HTTP_METHODS.map((m) => (
                <Option key={m} value={m}>
                  {m}
                </Option>
              ))}
            </Dropdown>

            <Dropdown
              className={styles.endpointDropdown}
              placeholder="Select an endpoint..."
              value={selectedEndpoint ? getEndpointDisplayName(selectedEndpoint) : ''}
              selectedOptions={selectedEndpoint ? [`${selectedEndpoint.method}:${selectedEndpoint.path}`] : []}
              onOptionSelect={(_, data) => {
                if (data.optionValue) {
                  const [m, ...pathParts] = data.optionValue.split(':')
                  const path = pathParts.join(':')
                  const endpoint = endpoints.find((e) => e.method === m && e.path === path)
                  if (endpoint) {
                    updateSelection(endpoint)
                  }
                }
              }}
            >
              {endpoints.map((endpoint) => {
                const displayName = getEndpointDisplayName(endpoint)
                return (
                  <Option
                    key={`${endpoint.method}:${endpoint.path}`}
                    value={`${endpoint.method}:${endpoint.path}`}
                    text={displayName}
                  >
                    <div className={styles.endpointOption}>
                      <span className={styles.endpointOptionName}>{displayName}</span>
                      <span className={styles.endpointOptionPath}>
                        <span className={getMethodBadgeClass(endpoint.method, styles)}>
                          {endpoint.method}
                        </span>
                        {endpoint.path}
                      </span>
                    </div>
                  </Option>
                )
              })}
            </Dropdown>

            <Button
              className={styles.sendButton}
              appearance="primary"
              icon={<SendRegular />}
              onClick={sendRequest}
              disabled={isLoading || !currentPath}
            >
              Send
            </Button>
          </div>
        </div>

        {/* URL Preview */}
        {currentPath && (
          <div className={styles.urlPreview}>
            <Text className={styles.urlText}>{resolvedUrl}</Text>
            <Tooltip content="Copy full URL" relationship="label">
              <Button
                className={styles.copyButton}
                appearance="subtle"
                icon={<CopyRegular />}
                size="small"
                onClick={copyUrl}
              />
            </Tooltip>
          </div>
        )}

        {/* Parameters Table */}
        <div className={styles.paramsSection}>
          <Text className={styles.sectionTitle}>Parameters</Text>
          <div className={styles.paramsTable}>
            <div className={styles.paramsHeader}>
              <Tooltip
                content={
                  <div className={styles.tooltipContent}>
                    <div>
                      <strong>Path:</strong> Replaces {'{id}'} placeholders in URL
                    </div>
                    <div>
                      <strong>Query:</strong> Appended as ?key=value
                    </div>
                    <div>
                      <strong>Header:</strong> Sent as HTTP headers
                    </div>
                  </div>
                }
                relationship="description"
              >
                <span style={{ cursor: 'help' }}>Type ⓘ</span>
              </Tooltip>
              <span>Name</span>
              <span>Value</span>
              <span></span>
            </div>
            {params.map((row) => (
              <div
                key={row.id}
                className={mergeClasses(styles.paramsRow, row.required && styles.paramsRowRequired)}
              >
                <Dropdown
                  className={styles.typeDropdown}
                  value={row.type}
                  selectedOptions={[row.type]}
                  disabled={row.required}
                  onOptionSelect={(_, data) => {
                    if (data.optionValue) {
                      updateParam(row.id, 'type', data.optionValue)
                    }
                  }}
                >
                  <Option value="Path">Path</Option>
                  <Option value="Query">Query</Option>
                  <Option value="Header">Header</Option>
                </Dropdown>
                <Input
                  className={styles.nameInput}
                  value={row.name}
                  disabled={row.required}
                  placeholder="Name"
                  onChange={(_, data) => updateParam(row.id, 'name', data.value)}
                />
                <Input
                  className={styles.valueInput}
                  value={row.value}
                  placeholder={row.required ? `Enter ${row.name}` : 'Value'}
                  onChange={(_, data) => updateParam(row.id, 'value', data.value)}
                />
                {!row.required ? (
                  <Button
                    className={styles.removeButton}
                    appearance="subtle"
                    icon={<DismissRegular />}
                    size="small"
                    onClick={() => removeParam(row.id)}
                  />
                ) : (
                  <span></span>
                )}
              </div>
            ))}
          </div>
          <div className={styles.addButtonRow}>
            <Button appearance="subtle" icon={<AddRegular />} size="small" onClick={addParam}>
              Add Parameter
            </Button>
            <Text className={styles.emptyHint}>Authorization header is added automatically</Text>
          </div>
        </div>

        {/* Request Body Section */}
        {['POST', 'PUT', 'PATCH'].includes(method) && (
          <div className={styles.bodySection}>
            <Text className={styles.sectionTitle}>Request Body</Text>
            <Textarea
              className={styles.bodyTextarea}
              placeholder="Select an endpoint to load example request body"
              value={body}
              onChange={(_, data) => setBody(data.value)}
              rows={6}
              resize="vertical"
            />
          </div>
        )}
      </div>

      {/* Right Panel - Response */}
      <div className={styles.responsePanel}>
        <div className={styles.panelHeader}>
          <Text className={styles.panelTitle}>Response</Text>
          {response && (
            <div className={styles.statusBadge}>
              <Badge color={getStatusColor(response.status)} appearance="filled">
                {response.status} {response.statusText}
              </Badge>
            </div>
          )}
        </div>

        {isLoading && (
          <div className={styles.loadingContainer}>
            <Spinner size="medium" />
            <Text>Sending request...</Text>
          </div>
        )}

        {error && (
          <div className={styles.responseBody}>
            <Text className={styles.errorText}>{error}</Text>
          </div>
        )}

        {response && !isLoading && (
          <div className={styles.responseBody}>{formatJson(response.body)}</div>
        )}

        {!response && !isLoading && !error && (
          <div className={styles.emptyState}>
            <Text>Service response</Text>
          </div>
        )}
      </div>
    </div>
  )
}
